import { ReactNode } from 'react';
import { Check, ClockAlert, Coffee, Plane, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Presence of a person today (HR Module Prototype `presenceOf`, Component Map › Profile bubble / Your
 * status / People status):
 * - `in` clocked in (success ring, check);
 * - `late` not clocked in on a work day (destructive);
 * - `break` on a break, `breaklong` on a break for 2 h+ (warning, the long one with a dashed ring);
 * - `leave` on leave (info ring, plane);
 * - `offsched` clocked in off-schedule: on a day off, a holiday or a leave day (success ring, info dot);
 * - `out` clocked out for the day (no ring, muted check);
 * - `notstarted` shift not started, `dayoff` off today (no ring, hollow dot);
 * - `off` nothing to show (still loading).
 */
export type Presence =
  | 'in'
  | 'late'
  | 'break'
  | 'breaklong'
  | 'leave'
  | 'offsched'
  | 'out'
  | 'notstarted'
  | 'dayoff'
  | 'off';

/** Ring colour; `null` for the states drawn without a ring. */
const RING: Record<Presence, string | null> = {
  in: 'border-success',
  late: 'border-destructive',
  break: 'border-warning',
  breaklong: 'border-warning border-dashed',
  leave: 'border-info',
  offsched: 'border-success',
  out: null,
  notstarted: null,
  dayoff: null,
  off: null,
};

/** Dot colour; `null` for a hollow, dashed dot. */
const DOT: Record<Presence, string | null> = {
  in: 'bg-success text-success-foreground',
  late: 'bg-destructive text-destructive-foreground',
  break: 'bg-warning text-warning-foreground',
  breaklong: 'bg-warning text-warning-foreground',
  leave: 'bg-info text-info-foreground',
  offsched: 'bg-info text-info-foreground',
  out: 'bg-muted text-muted-foreground',
  notstarted: null,
  dayoff: null,
  off: null,
};

const DOT_ICON: Record<Presence, LucideIcon | null> = {
  in: Check,
  late: ClockAlert,
  break: Coffee,
  breaklong: Coffee,
  leave: Plane,
  offsched: Plane,
  out: Check,
  notstarted: null,
  dayoff: null,
  off: null,
};

/** The colour of a presence's label (the prototype's `.p-*` text colours). */
export const PRESENCE_TEXT_TONE: Record<Presence, string> = {
  in: 'text-success',
  late: 'text-destructive',
  break: 'text-warning',
  breaklong: 'text-warning',
  leave: 'text-info',
  offsched: 'text-success',
  out: 'text-muted-foreground',
  notstarted: 'text-muted-foreground',
  dayoff: 'text-muted-foreground',
  off: 'text-muted-foreground',
};

/** Avatar sizes with their ring inset and dot size, as the prototype's `.av.sNN` rules. */
const SIZE = {
  24: {
    box: 'size-6 text-[10px]',
    ring: '-inset-[3px] border-[1.5px]',
    dot: 'size-[9px] -bottom-0.5 -right-0.5',
  },
  32: {
    box: 'size-8 text-xs',
    ring: '-inset-[3px] border-2',
    dot: 'size-3.5 -bottom-[3px] -right-[3px]',
  },
  40: {
    box: 'size-10 text-sm',
    ring: '-inset-[3px] border-2',
    dot: 'size-4 -bottom-[3px] -right-[3px]',
  },
} as const;

const HOLLOW_DOT = 'border-[1.5px] border-dashed border-muted-foreground bg-card';

type PresenceAvatarProps = {
  size?: keyof typeof SIZE;
  presence?: Presence;
  /** The picture, or the initials shown without one. */
  children: ReactNode;
  className?: string;
};

/**
 * A presence on its own (prototype `presenceSwatch`): a 24px ring in the presence colour around a 14px dot
 * carrying its icon (a check when clocked in). States without a ring show only their dot; `off` (still
 * loading), `notstarted` and `dayoff` show a hollow, dashed dot.
 */
export const PresenceSwatch = ({
  presence,
  className,
}: {
  presence: Presence;
  className?: string;
}) => {
  const DotIcon = DOT_ICON[presence];
  const ring = RING[presence];
  const dot = DOT[presence];
  return (
    <span aria-hidden className={cn('relative grid size-6 shrink-0 place-items-center', className)}>
      {ring && <span className={cn('absolute inset-0 rounded-full border-2 border-solid', ring)} />}
      {dot ? (
        <span className={cn('grid size-3.5 place-items-center rounded-full', dot)}>
          {DotIcon && <DotIcon strokeWidth={2.5} className="size-2.5" />}
        </span>
      ) : (
        <span className={cn('size-[18px] rounded-full', HOLLOW_DOT)} />
      )}
    </span>
  );
};

/** A round avatar with a presence ring and status dot; `off` draws neither. */
export const PresenceAvatar = ({
  size = 32,
  presence = 'off',
  children,
  className,
}: PresenceAvatarProps) => {
  const DotIcon = DOT_ICON[presence];
  const sizing = SIZE[size];
  const ring = RING[presence];
  const dot = DOT[presence];
  return (
    <span
      className={cn(
        'relative inline-flex shrink-0 select-none items-center justify-center rounded-full bg-muted font-medium text-muted-foreground',
        sizing.box,
        className
      )}
    >
      <span className="flex size-full items-center justify-center overflow-hidden rounded-full">
        {children}
      </span>
      {ring && (
        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute rounded-full border-solid',
            sizing.ring,
            ring
          )}
        />
      )}
      {presence !== 'off' && (
        <span
          aria-hidden
          className={cn(
            'absolute grid place-items-center rounded-full shadow-[0_0_0_2px_oklch(var(--background))]',
            sizing.dot,
            dot ?? HOLLOW_DOT
          )}
        >
          {DotIcon && dot && size !== 24 && <DotIcon strokeWidth={2.5} className="size-[70%]" />}
        </span>
      )}
    </span>
  );
};
